import type { LocationData } from '../types/moderation';

type Precision = LocationData['precision'];
const PRECISIONS: Precision[] = ['exact', 'approximate', 'suburb', 'city'];

interface MetadataLocationFieldsetProps {
  locationText: string;
  locationLat: string;
  locationLng: string;
  locationPrecision: Precision;
  /** Update a single subfield in the parent form state. */
  onChange: (
    field: 'locationText' | 'locationLat' | 'locationLng' | 'locationPrecision',
    value: string,
  ) => void;
  /** Per-field validation messages keyed by field name. */
  errors?: {
    locationLat?: string;
    locationLng?: string;
  };
}

/**
 * F-203 — focused Location fieldset extracted from MetadataEditor.
 *
 * Pure presentational: receives values + onChange + errors and emits
 * the right onChange call per subfield. Field IDs match the previous
 * data-testid contract so existing tests keep working.
 */
export default function MetadataLocationFieldset({
  locationText,
  locationLat,
  locationLng,
  locationPrecision,
  onChange,
  errors,
}: MetadataLocationFieldsetProps) {
  return (
    <fieldset className="form-fieldset" data-testid="field-location">
      <legend>Location</legend>
      <label className="form-group">
        <span>Location text</span>
        <input
          type="text"
          value={locationText}
          onChange={(e) => onChange('locationText', e.target.value)}
          data-testid="field-locationText"
        />
      </label>
      <div className="form-row">
        <label className="form-group form-group-half">
          <span>Latitude</span>
          <input
            type="text"
            inputMode="decimal"
            value={locationLat}
            onChange={(e) => onChange('locationLat', e.target.value)}
            placeholder="-27.470"
            data-testid="field-locationLat"
            aria-invalid={errors?.locationLat ? 'true' : undefined}
            aria-describedby={errors?.locationLat ? 'field-locationLat-error' : undefined}
          />
          {errors?.locationLat && (
            <div
              id="field-locationLat-error"
              className="field-error"
              role="alert"
              data-testid="field-locationLat-error"
            >
              {errors.locationLat}
            </div>
          )}
        </label>
        <label className="form-group form-group-half">
          <span>Longitude</span>
          <input
            type="text"
            inputMode="decimal"
            value={locationLng}
            onChange={(e) => onChange('locationLng', e.target.value)}
            placeholder="153.025"
            data-testid="field-locationLng"
            aria-invalid={errors?.locationLng ? 'true' : undefined}
            aria-describedby={errors?.locationLng ? 'field-locationLng-error' : undefined}
          />
          {errors?.locationLng && (
            <div
              id="field-locationLng-error"
              className="field-error"
              role="alert"
              data-testid="field-locationLng-error"
            >
              {errors.locationLng}
            </div>
          )}
        </label>
      </div>
      <label className="form-group">
        <span>Precision</span>
        <select
          value={locationPrecision}
          onChange={(e) => onChange('locationPrecision', e.target.value)}
          data-testid="field-locationPrecision"
        >
          {PRECISIONS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>
    </fieldset>
  );
}
